import { TableColumn, TableStyles } from 'react-data-table-component';
import { WfSalesOfficeSummaryResponse } from 'src/api/wf/models/WfSummary';

const fmt = (val?: number | null) => {
  if (val === undefined || val === null || isNaN(val)) return '0';
  return val.toLocaleString();
};

const formatRate = (val?: string | number | null) => {
  if (val === undefined || val === null || val === '-' || val === 'NaN') return '0.00';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '0.00';
  return (num * 100).toFixed(2);
};

const NUMERIC_COLUMN_WIDTH = '7rem';

export const mapSingleItemToBusinessSituation = (
  item: WfSalesOfficeSummaryResponse,
  nameOverride?: string
): WfSalesOfficeSummaryResponse => {
  return {
    ...item,
    salesOfficeName: nameOverride || item.salesOfficeName,
    sectionCode: item.sectionCode || '',
    sectionName: item.sectionName || '',
  };
};

export const businessSummaryCustomStyles: TableStyles = {
  tableWrapper: {
    style: {
      display: 'block',
      overflow: 'visible',
    },
  },
  table: {
    style: {
      backgroundColor: 'transparent',
      width: '100%',
      tableLayout: 'fixed',
    },
  },
  rows: {
    style: {
      minHeight: '2.25rem',
      borderBottom: '1px solid #d1d5db',
    },
  },
  cells: {
    style: {
      borderRight: '1px solid #d1d5db',
      padding: '0px',
      fontSize: '0.75rem',
    },
  },
};

const rightCol = (
  name: string,
  render: (row: WfSalesOfficeSummaryResponse) => string | number,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<WfSalesOfficeSummaryResponse> => ({
  name,
  selector: (row) => render(row).toString(),
  cell: (row) => {
    const isTotal = row.sectionCode === '';
    return (
      <div className={`flex w-full items-center justify-end pr-2 ${isTotal ? 'my-4' : ''}`}>
        {render(row)}
      </div>
    );
  },
  width,
});

const percentCol = (
  name: string,
  render: (row: WfSalesOfficeSummaryResponse) => string,
  width = NUMERIC_COLUMN_WIDTH
): TableColumn<WfSalesOfficeSummaryResponse> => ({
  name,
  selector: render,
  cell: (row) => <div className="w-full pr-2 text-right">{render(row)}%</div>,
  width,
});

export const businessSummaryColumns = (): TableColumn<WfSalesOfficeSummaryResponse>[] => {
  return [
    {
      name: 'Business Location',
      selector: (row) => row.salesOfficeName,
      cell: (row) => <div className="w-full text-center font-bold">{row.salesOfficeName}</div>,
      width: NUMERIC_COLUMN_WIDTH,
    },
    {
      name: 'Section',
      selector: (row) => [row.sectionCode, row.sectionName].filter(Boolean).join(' '),
      cell: (row) => (
        <div className="w-full text-center font-bold">
          {[row.sectionCode, row.sectionName].filter(Boolean).join(' ')}
        </div>
      ),
      width: NUMERIC_COLUMN_WIDTH,
    },
    rightCol('Products', (row) => fmt(row.products)),
    rightCol('Incidents', (row) => fmt(row.confinement?.incidentItems)),
    rightCol('Completed', (row) => fmt(row.confinement?.completedItems)),
    percentCol('Comp. Rate', (row) => formatRate(row.confinement?.completedRate)),
    rightCol('On-Call Rep.', (row) => fmt(row.currentSituation?.oncallReport)),
    rightCol('On Call', (row) => fmt(row.currentSituation?.onCall)),
    rightCol('Outage', (row) => fmt(row.currentSituation?.outage)),
    rightCol('Other Alarms', (row) => fmt(row.currentSituation?.otherAlerts)),
    rightCol('Parts', (row) => fmt(row.currentSituation?.parts)),
    rightCol('Flood', (row) => fmt(row.currentSituation?.flood)),
    rightCol('Inaccess.', (row) => fmt(row.currentSituation?.inaccessible)),
    rightCol('Restored', (row) => fmt(row.currentSituation?.restored)),
  ];
};
